
































import Vue from 'vue'
import Icon from './Icon.vue'
import { QueryParameters } from '@/router'

const FETCH_BANNER_REQUEST_URL = `${location.origin}/watchout/api/banner`

interface BannerResponse {
  id: number
  html: string
  kind?: string
  from?: string
  to?: string
}

function popupAdSessionKey (): string {
  return (window as any).__SITE_PRODUCT__ === 'lunchmap'
    ? 'lunchmapPopupAdSeen'
    : 'pubwatchPopupAdSeen'
}

function popupAdSeenThisSession (): boolean {
  try {
    return sessionStorage?.getItem(popupAdSessionKey()) === '1'
  } catch {
    return false
  }
}

function markPopupAdSeenThisSession (): void {
  try {
    sessionStorage?.setItem(popupAdSessionKey(), '1')
  } catch {}
}

function isAdsenseBanner (banner: BannerResponse): boolean {
  return banner.kind === 'adsense' || /adsbygoogle/i.test(banner.html || '')
}

const ADSENSE_CLIENT = 'ca-pub-5620005037726316'
const ADSENSE_SCRIPT_SRC = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`
const ADSENSE_WAIT_MS = 15000

function adsenseHasVisibleCreative (ins: HTMLElement): boolean {
  const status = ins.getAttribute('data-ad-status')
  if (status === 'unfilled') {
    return false
  }
  if (status === 'filled') {
    return true
  }
  const iframe = ins.querySelector('iframe')
  if (!iframe) {
    return false
  }
  const rect = iframe.getBoundingClientRect()
  return rect.height > 40 && rect.width > 40
}

function reviveScripts (root: HTMLElement) {
  Array.from(root.querySelectorAll('script')).forEach(old => {
    const next = document.createElement('script')
    Array.from(old.attributes).forEach(attr => next.setAttribute(attr.name, attr.value))
    next.textContent = old.textContent
    old.parentNode && old.parentNode.replaceChild(next, old)
  })
}

function ensureAdsenseReady (): Promise<void> {
  const win = window as any
  if (win.adsbygoogle && typeof win.adsbygoogle.push === 'function') {
    return Promise.resolve()
  }
  return new Promise(resolve => {
    const existing = document.querySelector('script[src*="adsbygoogle.js"]') as HTMLScriptElement | null
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true })
      existing.addEventListener('error', () => resolve(), { once: true })
      return
    }
    const script = document.createElement('script')
    script.async = true
    script.src = ADSENSE_SCRIPT_SRC
    script.crossOrigin = 'anonymous'
    script.onload = () => resolve()
    script.onerror = () => resolve()
    document.head.appendChild(script)
  })
}

function requestAdsenseFill (root: HTMLElement) {
  const ins = root.querySelector('ins.adsbygoogle') as HTMLElement | null
  if (!ins || ins.dataset.pwAdsenseRequested === '1') {
    return
  }
  ins.dataset.pwAdsenseRequested = '1'
  reviveScripts(root)
}

export default Vue.extend({
  components: {
    Icon
  },
  data () {
    return {
      visible: false,
      bannerHtml: '',
      bannerId: null as null | number,
      isAdsense: false,
      adsensePending: false,
      adsenseObserver: null as MutationObserver | null,
      adsenseTimer: 0 as number,
      previewMode: false,
      adsenseStarted: false
    }
  },
  beforeDestroy () {
    this.stopAdsenseWatch()
  },
  mounted () {
    const requestedBannerId = this.$route.query[QueryParameters.Banner]
    this.previewMode = Boolean(requestedBannerId)
    const requestURL = new URL(FETCH_BANNER_REQUEST_URL)
    requestedBannerId && requestURL.searchParams.set(QueryParameters.Banner, requestedBannerId as string)

    fetch(requestURL.toString())
      .then(response => response.ok ? response.json() : null)
      .then((banner: BannerResponse | null) => {
        if (!banner?.id || !banner.html) {
          return
        }

        if (!requestedBannerId && popupAdSeenThisSession()) {
          return
        }

        this.bannerId = banner.id
        this.bannerHtml = banner.html
        this.isAdsense = isAdsenseBanner(banner)

        if (this.isAdsense) {
          this.adsensePending = true
          this.$nextTick(() => this.waitForAdsenseFill())
          return
        }

        this.showBanner()
      })
      .catch(() => {})
  },
  methods: {
    stopAdsenseWatch () {
      if (this.adsenseObserver) {
        this.adsenseObserver.disconnect()
        this.adsenseObserver = null
      }
      if (this.adsenseTimer) {
        window.clearTimeout(this.adsenseTimer)
        this.adsenseTimer = 0
      }
    },
    showBanner () {
      this.visible = true
      this.adsensePending = false
      if (this.isAdsense) {
        this.$nextTick(() => this.fitAdsenseLayout())
      }
    },
    discardBanner () {
      this.stopAdsenseWatch()
      this.visible = false
      this.adsensePending = false
      this.bannerHtml = ''
    },
    waitForAdsenseFill () {
      if (this.adsenseStarted) {
        return
      }
      this.adsenseStarted = true
      this.stopAdsenseWatch()
      const root = this.$refs.bannerContent as HTMLElement | undefined
      if (!root) {
        this.discardBanner()
        return
      }
      ensureAdsenseReady().then(() => {
        if (!this.adsensePending) {
          return
        }
        requestAdsenseFill(root)
        const ins = root.querySelector('ins.adsbygoogle') as HTMLElement | null
        if (!ins) {
          this.discardBanner()
          return
        }
        const inspect = () => {
          if (ins.getAttribute('data-ad-status') === 'unfilled') {
            this.discardBanner()
            return true
          }
          if (adsenseHasVisibleCreative(ins)) {
            this.showBanner()
            return true
          }
          return false
        }
        if (inspect()) {
          return
        }
        if (window.MutationObserver) {
          this.adsenseObserver = new MutationObserver(inspect)
          this.adsenseObserver.observe(ins, { attributes: true, attributeFilter: ['data-ad-status'] })
        }
        this.adsenseTimer = window.setTimeout(() => {
          if (adsenseHasVisibleCreative(ins)) {
            this.showBanner()
          } else {
            this.discardBanner()
          }
        }, ADSENSE_WAIT_MS)
      })
    },
    closeBanner () {
      markPopupAdSeenThisSession()
      this.stopAdsenseWatch()
      this.visible = false
      this.adsensePending = false
      this.bannerHtml = ''
      this.adsenseStarted = false
    },
    fitAdsenseLayout () {
      const root = this.$refs.bannerContent as HTMLElement | undefined
      if (!root) {
        return
      }
      const ins = root.querySelector('ins.adsbygoogle') as HTMLElement | null
      const iframe = ins?.querySelector('iframe') as HTMLIFrameElement | null
      if (!ins || !iframe) {
        return
      }
      const sync = () => {
        const rect = iframe.getBoundingClientRect()
        if (rect.height > 0) {
          ins.style.minHeight = `${Math.ceil(rect.height)}px`
        }
        if (rect.width > 0) {
          ins.style.width = `${Math.ceil(rect.width)}px`
          ins.style.maxWidth = '100%'
        }
      }
      sync()
      window.setTimeout(sync, 100)
      window.setTimeout(sync, 500)
      const resizeObserverCtor = (window as any).ResizeObserver
      if (resizeObserverCtor) {
        new resizeObserverCtor(sync).observe(iframe)
      }
    }
  }
})
